import { api } from './http'
import type { Page, QueryParams, StudentSummary, UUID } from './types'

export const CHANNELS = ['telegram', 'vk', 'avito', 'max'] as const

export type CommunicationChannel = typeof CHANNELS[number]
export type ChannelMessageDirection = 'inbound' | 'outbound'
export type ChannelMessageType =
  | 'text'
  | 'image'
  | 'link'
  | 'voice'
  | 'audio'
  | 'document'
  | 'item'
  | 'service'

export interface ChannelConnection {
  id: UUID
  version?: number
  channel: CommunicationChannel
  providerAccountId: string
  baseUrl?: string | null
  mediaUrl?: string | null
  title: string
  color?: string | null
  enabled: boolean
  status?: string | null
  username?: string | null
  externalUrl?: string | null
  webhookSubscribed?: boolean
  credentialConfigured?: boolean
  adminChatId?: string | null
  lastSyncAt?: string | null
  errorMessage?: string | null
}

/** Save payload for the channel account CRUD (POST/PUT /channel-integrations/accounts). */
export interface ChannelConnectionSaveCommand {
  provider: CommunicationChannel
  name: string
  remoteTenantId?: string | null
  baseUrl: string
  /** Write-only. Omit on update to preserve credentials already stored in PostgreSQL. */
  credentials?: Record<string, string> | null
  config?: Record<string, unknown> | null
  /** Compatibility fallback for connections created before DB-managed credentials. */
  credentialRef?: string | null
  status: 'active' | 'disabled'
  version?: number
}

export interface ChannelStudent {
  id: UUID
  name: string
  phone?: string | null
}

export interface ChannelConversation {
  id: UUID
  version?: number
  channel: CommunicationChannel
  accountId: UUID
  accountTitle: string
  accountColor?: string | null
  providerConversationId: string
  peerId: string
  peerName?: string | null
  peerPhone?: string | null
  peerPhoneSource?: 'max' | 'profile' | null
  peerUsername?: string | null
  peerPhotoUrl?: string | null
  subject?: string | null
  subjectUrl?: string | null
  students: ChannelStudent[]
  staff?: Array<{ staff: { id: UUID; name: string }; role: string }>
  lastMessageText?: string | null
  lastMessageAt?: string | null
  lastMessageDirection?: ChannelMessageDirection | null
  unreadCount: number
  allowMessages?: boolean
  blocked?: boolean
  metadata?: Record<string, unknown>
}

export interface ChannelImageContent {
  previewUrl?: string | null
  url?: string | null
  fileId?: string | null
  file_id?: string | null
  alt?: string | null
}

export interface ChannelLinkContent {
  url: string
  title?: string | null
}

export interface ChannelVoiceContent {
  url?: string | null
  durationSeconds?: number | null
  mimeType?: string | null
}

export interface ChannelDocumentContent {
  url?: string | null
  name?: string | null
  mimeType?: string | null
}

export interface ChannelItemContent {
  url?: string | null
  title?: string | null
}

export interface ChannelServiceContent {
  text?: string | null
}

export interface ChannelKeyboardButton {
  text: string
  url?: string | null
  callbackData?: string | null
}

export interface ChannelMessageContent {
  operatorPlainText?: boolean
  /** MAX history may identify an edit without providing its timestamp. */
  max?: { isEdited?: boolean } | null
  image?: ChannelImageContent | null
  link?: ChannelLinkContent | null
  voice?: ChannelVoiceContent | null
  document?: ChannelDocumentContent | null
  item?: ChannelItemContent | null
  service?: ChannelServiceContent | null
  /** Inline buttons attached to the message (Telegram reply_markup.inline_keyboard). */
  keyboard?: ChannelKeyboardButton[][] | null
}

export interface ChannelMessage {
  id: UUID
  authorName?: string | null
  channel?: CommunicationChannel
  version?: number
  providerMessageId: string
  conversationId: UUID
  direction: ChannelMessageDirection
  type: ChannelMessageType
  text?: string | null
  content?: ChannelMessageContent | null
  /** Legacy read flag; use status for recipient receipts on outgoing messages. */
  read: boolean
  createdAt: string
  status?: string | null
  /** Actual recipient read time reported by the provider, when available. */
  readAt?: string | null
  label?: string | null
  /** When set, the message was edited in the source channel; revisions hold prior snapshots. */
  editedAt?: string | null
  deletedAt?: string | null
  actions?: {
    canEdit: boolean
    canDelete: boolean
    editUnavailableReason?: string | null
    deleteUnavailableReason?: string | null
    maxTextLength: number
  } | null
}

/** A previous body/content snapshot captured before an edit overwrote the live message. */
export interface ChannelMessageRevision {
  revisionNo: number
  body: string
  content: ChannelMessageContent | null
  type: ChannelMessageType
  editedAt: string
}

export interface ChannelConversationPage extends Page<ChannelConversation> {
  allCount: number
  unreadCount: number
  linkedCount: number
  unassignedCount: number
}

export interface ChannelMessagePage extends Page<ChannelMessage> {
  hasMore?: boolean
}

export interface QuickReplyTemplate {
  id: UUID
  version?: number
  channel: CommunicationChannel | null
  title: string
  text: string
  imageUrl?: string | null
  sortOrder: number
}

export interface ChannelConversationQuery extends QueryParams {
  /** Match linked students' names only, independently of provider identities. */
  studentName?: string
  channel: CommunicationChannel
  accountId?: UUID
  studentId?: UUID
  staffId?: UUID
  unassigned?: boolean
  unread?: boolean
  search?: string
  page?: number
  size?: number
}

export interface MaxContact {
  accountId: UUID
  chatId: string
  phone: string
  name?: string | null
  avatarUrl?: string | null
  conversationId?: UUID | null
}

export interface QuickReplySaveCommand {
  channel: CommunicationChannel | null
  applyChannel?: boolean
  title: string
  text: string
  version?: number
}

type CollectionWire<T> =
  | T[]
  | Partial<Page<T>> & {
      content?: T[]
      items?: T[]
      conversations?: T[]
      messages?: T[]
      total?: number
      allCount?: number
      unreadCount?: number
      linkedCount?: number
      unassignedCount?: number
      hasMore?: boolean
    }

function collection<T>(source: CollectionWire<T>, preferred: 'conversations' | 'messages' | 'items'): T[] {
  if (Array.isArray(source)) return source
  return source.content ?? source[preferred] ?? source.items ?? []
}

function pageValue(source: CollectionWire<unknown>, key: 'page' | 'size' | 'totalElements' | 'totalPages'): number | undefined {
  if (Array.isArray(source)) return undefined
  const value = source[key]
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

export function normalizeConversationPage(source: CollectionWire<ChannelConversation>): ChannelConversationPage {
  const content = collection(source, 'conversations')
  const page = pageValue(source, 'page') ?? 0
  const size = pageValue(source, 'size') ?? Math.max(content.length, 1)
  const totalElements = Array.isArray(source)
    ? source.length
    : (pageValue(source, 'totalElements') ?? source.total ?? content.length)
  const totalPages = pageValue(source, 'totalPages') ?? Math.ceil(totalElements / size)
  const allCount = Array.isArray(source) ? source.length : (source.allCount ?? totalElements)
  const unassignedCount = Array.isArray(source) ? 0 : (source.unassignedCount ?? 0)

  return {
    content,
    page,
    size,
    totalElements,
    totalPages,
    allCount,
    unreadCount: Array.isArray(source)
      ? content.filter(item => item.unreadCount > 0).length
      : (source.unreadCount ?? content.filter(item => item.unreadCount > 0).length),
    linkedCount: Array.isArray(source)
      ? content.filter(item => item.students.length > 0).length
      : (source.linkedCount ?? Math.max(allCount - unassignedCount, 0)),
    unassignedCount,
  }
}

export function normalizeMessagePage(source: CollectionWire<ChannelMessage>): ChannelMessagePage {
  const content = collection(source, 'messages')
  const page = pageValue(source, 'page') ?? 0
  const size = pageValue(source, 'size') ?? Math.max(content.length, 1)
  const totalElements = Array.isArray(source)
    ? source.length
    : (pageValue(source, 'totalElements') ?? source.total ?? content.length)

  return {
    content,
    page,
    size,
    totalElements,
    totalPages: pageValue(source, 'totalPages') ?? Math.ceil(totalElements / size),
    hasMore: Array.isArray(source) ? false : source.hasMore,
  }
}

const BASE = '/channel-inbox'
const CONNECTIONS = '/channel-integrations/accounts'

function sendAttachment(conversationId: UUID, file: File) {
  const form = new FormData()
  form.append('file', file)
  return api.postForm<ChannelMessage>(`${BASE}/conversations/${conversationId}/attachments`, form)
}

export const channelInboxApi = {
  resolveMaxContact: (accountId: UUID, phone: string) =>
    api.post<MaxContact>(`${BASE}/max/contacts/resolve`, { accountId, phone }),

  openMaxConversation: (accountId: UUID, phone: string) =>
    api.post<ChannelConversation>(`${BASE}/max/conversations`, { accountId, phone }),

  connections: (channel: CommunicationChannel, signal?: AbortSignal) =>
    api.get<ChannelConnection[]>(CONNECTIONS, { channel }, signal),

  conversations: async (query: ChannelConversationQuery, signal?: AbortSignal) =>
    normalizeConversationPage(await api.get<CollectionWire<ChannelConversation>>(
      `${BASE}/conversations`,
      query,
      signal,
    )),

  conversation: (conversationId: UUID, signal?: AbortSignal) =>
    api.get<ChannelConversation>(`${BASE}/conversations/${conversationId}`, undefined, signal),

  messages: async (conversationId: UUID, options?: { directOnly?: boolean }, signal?: AbortSignal) =>
    normalizeMessagePage(await api.get<CollectionWire<ChannelMessage>>(
      `${BASE}/conversations/${conversationId}/messages`,
      { page: 0, size: 200, directOnly: options?.directOnly || undefined },
      signal,
    )),

  messageRevisions: (conversationId: UUID, messageId: UUID, signal?: AbortSignal) =>
    api.get<ChannelMessageRevision[]>(
      `${BASE}/conversations/${conversationId}/messages/${messageId}/revisions`,
      undefined,
      signal,
    ),

  sendMessage: (conversationId: UUID, text: string, idempotencyKey?: string) =>
    api.post<ChannelMessage>(`${BASE}/conversations/${conversationId}/messages`, {
      text,
      ...(idempotencyKey ? { idempotencyKey } : {}),
    }),

  sendAttachment,

  editMessage: (conversationId: UUID, messageId: UUID, text: string, version: number) =>
    api.put<ChannelMessage>(`${BASE}/conversations/${conversationId}/messages/${messageId}`, { text, version }),

  deleteMessage: (conversationId: UUID, messageId: UUID, version: number) =>
    api.delete<ChannelMessage>(`${BASE}/conversations/${conversationId}/messages/${messageId}?version=${version}`),

  /** Compatibility for older callers; images now use the same attachment endpoint. */
  sendImage: sendAttachment,

  markRead: (conversationId: UUID) =>
    api.post<ChannelConversation>(`${BASE}/conversations/${conversationId}/read`),

  markUnread: (conversationId: UUID) =>
    api.post<ChannelConversation>(`${BASE}/conversations/${conversationId}/unread`),

  setBlocked: (conversationId: UUID, blocked: boolean) =>
    api.post<ChannelConversation>(`${BASE}/conversations/${conversationId}/blocked`, { blocked }),

  setBotEnabled: (conversationId: UUID, enabled: boolean) =>
    api.post<ChannelConversation>(`${BASE}/conversations/${conversationId}/bot-enabled`, { enabled }),

  linkStudent: (conversationId: UUID, studentId: UUID) =>
    api.post<ChannelConversation>(`${BASE}/conversations/${conversationId}/students/${studentId}`),

  unlinkStudent: (conversationId: UUID, studentId: UUID) =>
    api.delete<ChannelConversation>(`${BASE}/conversations/${conversationId}/students/${studentId}`),

  studentLink: (
    channel: CommunicationChannel,
    accountId: UUID,
    studentId: UUID,
    signal?: AbortSignal,
  ) => api.get<{ url: string }>(`${BASE}/student-link`, {
    channel,
    accountId,
    studentId,
  }, signal),

  sync: (channel: CommunicationChannel, accountId?: UUID | null) =>
    api.post<void>(`${BASE}/sync`, { channel, accountId: accountId || undefined }),

  subscribeWebhook: (accountId: UUID) =>
    api.post<ChannelConnection>(`${CONNECTIONS}/${accountId}/webhook`),

  unsubscribeWebhook: (accountId: UUID) =>
    api.delete<ChannelConnection>(`${CONNECTIONS}/${accountId}/webhook`),

  connection: (accountId: UUID, signal?: AbortSignal) =>
    api.get<ChannelConnection>(`${CONNECTIONS}/${accountId}`, undefined, signal),

  createConnection: (command: ChannelConnectionSaveCommand) =>
    api.post<ChannelConnection>(CONNECTIONS, command),

  updateConnection: (accountId: UUID, command: ChannelConnectionSaveCommand) =>
    api.put<ChannelConnection>(`${CONNECTIONS}/${accountId}`, command),

  deleteConnection: (accountId: UUID, version: number) =>
    api.delete<void>(`${CONNECTIONS}/${accountId}`, { version }),

  templates: (signal?: AbortSignal) =>
    api.get<QuickReplyTemplate[]>(`${BASE}/templates`, undefined, signal),

  createTemplate: (command: QuickReplySaveCommand) =>
    api.post<QuickReplyTemplate>(`${BASE}/templates`, command),

  updateTemplate: (templateId: UUID, command: QuickReplySaveCommand) =>
    api.put<QuickReplyTemplate>(`${BASE}/templates/${templateId}`, command),

  deleteTemplate: (templateId: UUID, version?: number) => version === undefined
    ? api.delete<void>(`${BASE}/templates/${templateId}`)
    : api.delete<void>(`${BASE}/templates/${templateId}`, { version }),

  searchStudents: (search: string, signal?: AbortSignal) =>
    api.get<Page<StudentSummary>>('/students', {
      q: search,
      archived: false,
      page: 0,
      size: 20,
      sort: 'displayName',
      direction: 'asc',
    }, signal),
}
